import { NgClass } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DataStoreKey } from '@compiere-ws/models/compiere-data-json';
import { DetailsNoteService } from '@compiere-ws/services/details-note/details-note.service';
import { DocServerService } from '@compiere-ws/services/doc-server/doc-server.service';
import { UploadedFile } from '@iupics-components/models/uploaded-file';
import InputMentionUiComponent from '@iupics-components/standard/fields/input-mention-ui/input-mention-ui.component';
import EditTabUiComponent from '@iupics-components/standard/layouts/edit-tab-ui/edit-tab-ui.component';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import { ModalContentType } from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import { CacheManagerService } from '@iupics-manager/managers/cache-manager/cache-manager.service';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { AbstractDynamicView } from '@iupics-manager/models/abstract-dynamic-view';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsEvent, IupicsTypeEvent } from '@iupics-manager/models/iupics-event';
import { UserAccount } from '@iupics-manager/models/user-account';
import { DocIconPipe } from '@iupics-util/pipes/doc-icon/doc-icon.pipe';
import { DateUtils } from '@iupics-util/tools/date.utils';
import { SubscriptionsUtils } from '@iupics-util/tools/subscriptions.util';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, filter, tap } from 'rxjs';
import JoinFileUiComponent from '../../../specific/window/join-file-ui/join-file-ui.component';
import ModalUiComponent from '../../layouts/modal-ui/modal-ui.component';
import NoteUiComponent from '../note-ui/note-ui.component';
import { ChatEntry, ChatTag, NoteEntry } from '../notes.interface';
@Component({
  selector: 'iu-notes-panel-ui',
  templateUrl: './notes-panel-ui.component.html',
  styleUrls: ['./notes-panel-ui.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgClass,
    NoteUiComponent,
    FormsModule,
    InputMentionUiComponent,
    TooltipModule,
    forwardRef(() => ModalUiComponent),
    JoinFileUiComponent,
    TranslateModule,
    DocIconPipe,
  ],
})
export default class NotesPanelUiComponent
  extends AbstractDynamicView
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  readonly #connectorService = inject(SecurityManagerService);
  readonly #detailsNoteService = inject(DetailsNoteService);
  readonly #docServerService = inject(DocServerService);

  @Input() table_id: number;
  @Input() currentDataStoreKey: DataStoreKey;
  @Input() parentEdit;

  @ViewChild('notesContainer') notesContainer: ElementRef<HTMLElement>;
  @ViewChild('notesWrapper') notesWrapper: ElementRef<HTMLElement>;

  subjectNote: string;
  isNotesLoaded = false;
  isNoteSending = false;

  //#region enum for template
  ModalContentType = ModalContentType;

  isFollow = false;
  chatID = -1;
  recordID: number;
  windowID: number;
  notes: NoteEntry[];
  displayJoinFilesPanel = false;
  entryAttachment: UploadedFile = null;

  /* Users infos */
  me: UserAccount;

  /* Input note */
  @ViewChild(InputMentionUiComponent) inputMentionRef: InputMentionUiComponent;
  isTagsLoaded = false;
  tags: ChatTag[];
  tags$ = new BehaviorSubject<ChatTag[]>([]);

  ngOnInit(): void {
    this.me = this.#connectorService.getIupicsUserAccount();
    this.entryAttachment = null;
    this.loadNotesData();
    (this.parentEdit ?? this.parentTab.editViewParent).notifierLinkedComponent
      .pipe(
        filter((event: any) => event.type === IupicsTypeEvent.UPDATE_ROWSAVED),
        tap(() => this.loadNotesData()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.scrollNotesIntoView();
  }

  ngAfterViewChecked(): void {
    if (!this.isNotesLoaded) {
      this.scrollToEnd();
    }
  }

  ngOnDestroy() {
    SubscriptionsUtils.unsubscribe(...this.subscriptions);
  }

  onSiblingUpdate(event: IupicsEvent): void {
    this.loadNotesData();
  }

  onRemoveComponent(event: IupicsEvent): void {
    if (event.type === IupicsTypeEvent.removeChildEvent) {
      event.type = IupicsTypeEvent.removeBreadcrumbItem;
      this.DOMParentComponent.onChildUpdate(event);
      this.notifierLinkedComponent.next({
        type: IupicsTypeEvent.expandEvent,
        item: undefined,
      });
    }
  }

  addTabToEditView(editTabUi: EditTabUiComponent): void {}

  loadNotesData() {
    this.isNotesLoaded = false;
    if (this.parentEdit) {
      this.DOMParentComponent = this.parentEdit;
    }
    this.table_id = (this.DOMParentComponent as EditViewUiComponent).table_id;
    this.currentDataStoreKey = (this.DOMParentComponent as EditViewUiComponent).currentDataStoreKey;
    this.recordID = parseInt(this.currentDataStoreKey.recordId.split(',')[1]);
    this.windowID = this.currentDataStoreKey.windowId;
    if (!this.isNoteEmpty()) {
      this.clearText();
      this.entryAttachment = null;
    }
    this.getChatTags();
    this.showNotes();

    const dataStored = (this.DOMParentComponent as EditViewUiComponent).editTabs?.[0]?.dataStored?.data;
    let subject = dataStored?.['Name'];
    subject ??= dataStored?.['Value'];
    subject ??= dataStored?.['DocumentNo'];
    subject ??= `ID : ${this.currentDataStoreKey.recordId.split(',')[1]}`;
    this.subjectNote = (this.DOMParentComponent as EditViewUiComponent).data.label + ' — ' + subject;
  }

  showNotes() {
    this.notes = [];
    this.#detailsNoteService.getChatEntry(this.table_id, this.recordID).subscribe((chat) => {
      if (chat) {
        this.isFollow = chat.follow;
        this.chatID = chat.chatID;
        this.notes = chat?.chatEntries?.map((chatEntry) => this.parseNoteData(chatEntry)) ?? [];
        this.isNotesLoaded = true;
      }
    });
  }

  getChatTags() {
    if (CacheManagerService.chatTags[this.windowID]) {
      this.tags = CacheManagerService.chatTags[this.windowID];
      this.tags$.next(CacheManagerService.chatTags[this.windowID]);
      this.isTagsLoaded = true;
    } else {
      this.subscriptions.push(
        this.#detailsNoteService.getChatTags(this.windowID).subscribe({
          next: (tags) => {
            CacheManagerService.chatTags[this.windowID] = tags;
            this.tags = tags;
            this.tags$.next(tags);
            this.isTagsLoaded = true;
          },
        })
      );
    }
  }

  sendNote() {
    if (this.isNoteSending || this.isNoteEmpty()) {
      return;
    }

    const creatAt = new Date().toUTCString();
    const chatEntryToSend: ChatEntry = {
      chatEntryID: this.chatID,
      //created : creatAt.substring(0, 26) + creatAt.substring(27, creatAt.length),
      characterData: this.getText(),
      taggedUsers: this.getMentions(),
      subject: this.subjectNote,
      chatID: this.chatID,
      tableID: this.table_id,
      recordID: this.recordID,
      windowID: this.windowID,
      userID: this.me.id,
      userName: this.me.avatarInfos.fullname, // retrieve ad user fullname
      chatEntryAttachments: [],
    };

    if (this.entryAttachment != null) {
      chatEntryToSend.chatEntryAttachments = [
        {
          downloadURL: `${this.entryAttachment.src}`,
          fileName: `${this.entryAttachment.name}`,
          chatEntryID: 0,
          cm_ChatEntryAttachmentID: 0,
        },
      ];
    }

    this.isNoteSending = true;
    this.#detailsNoteService.sendChatEntry(chatEntryToSend).subscribe((chatEntry) => {
      if (chatEntry) {
        this.entryAttachment = null;
        if (this.chatID == -1) {
          this.chatID = chatEntry.chatID;
        }

        chatEntry.created = creatAt;

        this.clearText();
        this.notes.push(this.parseNoteData(chatEntry));
        this.scrollToEnd();
        this.isNoteSending = false;
      }
    });
  }

  parseNoteData(chatEntry: ChatEntry): NoteEntry {
    return {
      createdAt: DateUtils.formatLongLocaleStr(chatEntry?.created ?? new Date()),
      author: {
        id: chatEntry.userID,
        fullname: chatEntry.userName,
        // pictureURL:
      },
      content: chatEntry.characterData,
      mentions: chatEntry.taggedUsers,
      attachements: (chatEntry.chatEntryAttachments ?? []).map((attachment) => {
        return {
          src: attachment.downloadURL,
          filename: attachment.fileName,
          type: attachment?.fileName?.split('.')?.[attachment.fileName.split('.').length - 1] ?? undefined,
        };
      }),
    };
  }

  follow(action) {
    if (!action) {
      this.#detailsNoteService.deleteFollowChat(this.chatID, this.me.id).subscribe((result) => {
        if (result) {
          this.isFollow = action;
        }
      });
    } else {
      this.#detailsNoteService.followChat(this.chatID, this.me.id, this.table_id, this.recordID).subscribe((result) => {
        if (result) {
          this.chatID = result.chatID;
          this.isFollow = action;
        }
      });
    }
  }

  fileUpload(event) {
    this.entryAttachment = event;
  }

  deleteEntryAttachment() {
    this.subscriptions.push(
      this.#docServerService.deleteDocument(this.entryAttachment, false).subscribe((res) => {
        this.entryAttachment = null;
      })
    );
  }

  downloadEntryAttachment(attachment) {
    const sub = this.#docServerService.downloadDocument(attachment.src as string).subscribe((response) => {
      Global.downloadFile(response, attachment.filename as string);
      sub.unsubscribe();
    });
  }

  isNoteEmpty() {
    const text = this.getText();
    return (text === null || text === undefined || text.trim().length === 0) && this.entryAttachment === null;
  }

  getText() {
    if (this.inputMentionRef) {
      return this.inputMentionRef.getContent();
    }

    return null;
  }

  getMentions() {
    return this.inputMentionRef
      .getMentions()
      .map((m) => m.value)
      .join(',');
  }

  clearText() {
    this.inputMentionRef.setContent('');
  }

  scrollNotesIntoView() {
    if (this?.parentEdit?.scrollTo) {
      setTimeout(() => {
        this.parentEdit.scrollTo();
      }, 220);
    }
  }

  scrollToEnd() {
    setTimeout(() => {
      if (this.notesWrapper?.nativeElement) {
        this.notesWrapper.nativeElement.scrollTop = this.notesWrapper.nativeElement.scrollHeight;
      }
    }, 0);
  }
}
